import { useSelector } from 'react-redux';
import { DialogSize } from '../../../../../../../react-commons/components/data-display/dialog/ResizableDialog';
import { useMultiForm } from '../../../../../../../react-commons/hooks/useMultiForm';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultVideoAttachmentContentResource,
    useVideoAttachmentContentActions,
    VideoAttachmentContentMutationResourceSchema,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentContentResource';
import {
    defaultVideoAttachmentResource,
    useVideoAttachmentActions,
    VideoAttachmentMutationResourceSchema,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentResource';
import { EditChildBaseDialog } from '../../../../reusable-components/dialogs/EditChildBaseDialog';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { VideoAttachmentContent } from './VideoAttachmentContent';

export const VideoAttachmentEditDialog = ({
    onClose,
    objectId,
    versionedInId,
}: {
    onClose: () => void;
    objectId: string | undefined | null;
    versionedInId: string;
}) => {
    const readOnly = useSelector(selectReadOnly);
    const validationScope = useSelector(selectNoScopeValidationScope);

    const videoAttachmentActions = useVideoAttachmentActions();
    const videoAttachmentContentActions = useVideoAttachmentContentActions();
    const videoAttachmentQuery = videoAttachmentActions.useGetResourceByObjectIdAndVersionedIn(objectId, versionedInId);
    const videoAttachmentContentQuery = videoAttachmentContentActions.useGetSingleResource(
        videoAttachmentQuery.data?.id
    );
    const updateVideoAttachment = videoAttachmentActions.useUpdateResource();
    const updateVideoAttachmentContent = videoAttachmentContentActions.useUpdateResource();
    const addVideoAttachmentContent = videoAttachmentContentActions.useSetResource();
    const validation = videoAttachmentActions.useValidateResource(videoAttachmentQuery.data?.id, validationScope);
    const validateVideoAttachmentContent = videoAttachmentContentActions.useValidateResource(
        videoAttachmentContentQuery.data?.id,
        validationScope
    );

    const { form: videoForm, formNames: videoFormNames } = useMyForm({
        schema: VideoAttachmentMutationResourceSchema,
        defaultValues: defaultVideoAttachmentResource,
        reactiveValues: videoAttachmentQuery.data,
    });
    const { form: videoContentForm, formNames: videoContentFormNames } = useMyForm({
        schema: VideoAttachmentContentMutationResourceSchema,
        defaultValues: defaultVideoAttachmentContentResource,
        reactiveValues: videoAttachmentContentQuery.data,
    });
    const { onSubmit, form } = useMultiForm(
        {
            primary: videoForm,
            secondary: videoContentForm,
        },
        async (data) => {
            if (videoForm.formState.isDirty) {
                await updateVideoAttachment.mutateAsync({
                    resource: data.primary,
                    id: data.primary.id,
                });
            }
            if (!data.secondary.id) {
                await addVideoAttachmentContent.mutateAsync({
                    resource: data.secondary,
                    parentId: data.primary.id,
                    withoutGrowl: true,
                });
            } else if (data.secondary.id && videoContentForm.formState.isDirty) {
                await updateVideoAttachmentContent.mutateAsync({
                    resource: data.secondary,
                    id: data.secondary.id,
                });
            }
        }
    );

    return (
        <EditChildBaseDialog
            onClose={onClose}
            edited={form.formState.isDirty}
            title={'Edit Video Attachment'}
            resourceType={[ResourceType.VIDEO_ATTACHMENT, ResourceType.VIDEO_ATTACHMENT_CONTENT]}
            resourceValidationResult={
                validation.data && validateVideoAttachmentContent.data ?
                    [validation.data, validateVideoAttachmentContent.data]
                :   validation.data
            }
            metaDataInfo={videoAttachmentQuery.data}
            dialogSize={DialogSize.MEDIUM_LARGE}
        >
            <QueryBaseComponent name="VideoAttachment" query={videoAttachmentQuery}>
                <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                    <VideoAttachmentContent
                        form={videoForm}
                        formNames={videoFormNames}
                        contentForm={videoContentForm}
                        contentFormNames={videoContentFormNames}
                        readOnly={readOnly}
                        id={videoAttachmentQuery.data?.id}
                    />
                </Form_DisplayComponentBaseView>
            </QueryBaseComponent>
        </EditChildBaseDialog>
    );
};
